import { handleApiResponse } from 'utilities/apiUtils';
import { formatUsPhone, maskUsPhone } from 'utilities/formatPhone';
import React from 'react';
import { useSelectedClient } from '../../../hooks/useSelectedClient';
import AddCertificateHolder from './AddCertificateHolder';
import PhoneBook from './PhoneBook';
import FaxCoverPage, { CoverPageData, buildCoverPageXml } from './FaxCoverPage';
import Step6MassSection from './Step6';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import { AutoComplete } from 'primereact/autocomplete';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';

export type Step1Props = {
  email: string;
  sendToEmail: boolean;
  ccToMe: boolean;
  copyToInsuredEmail: string[];
  certificateHolder: { label: string; value: string }[];
  setEmail: (v: string) => void;
  setSendToEmail: (v: boolean) => void;
  setCcToMe: (v: boolean) => void;
  setCopyToInsuredEmail: (v: string[]) => void;
  setCertificateHolder: (v: { label: string; value: string }[]) => void;
  insuredDatabaseId?: string;
  insuredEmail?: string;
  setIsEmailValid?: (valid: boolean) => void;
  fax: string;
  setFax: (v: string) => void;
  insuredFax: string;
  setInsuredFax: (v: string) => void;
  onClientDetails?: (details: { firstName: string; lastName: string; commercialName: string; stateName: string }) => void;
  selectedSentCertIds?: string[];
  setSelectedSentCertIds?: (ids: string[]) => void;
  sendToInsuredEmail: boolean;
  setSendToInsuredEmail: (v: boolean) => void;
  sendToInsuredFax: boolean;
  setSendToInsuredFax: (v: boolean) => void;
  sendToFax: boolean;
  setSendToFax: (v: boolean) => void;
  onCoverPageSave: (xml: string | null) => void;
  selectedMasterCertificates: string[];
  setSelectedMasterCertificates: (v: string[]) => void;
};

export interface Step1Ref { validate: () => boolean; }

const Step1 = React.forwardRef<Step1Ref, Step1Props>(function Step1InnerWithRef({
  email,
  sendToEmail,
  ccToMe,
  copyToInsuredEmail,
  certificateHolder,
  setEmail,
  setSendToEmail,
  setCcToMe,
  setCopyToInsuredEmail,
  setCertificateHolder,
  insuredDatabaseId,
  insuredEmail,
  setIsEmailValid,
  fax,
  setFax,
  insuredFax,
  setInsuredFax,
  onClientDetails,
  selectedSentCertIds,
  setSelectedSentCertIds,
  sendToInsuredEmail,
  setSendToInsuredEmail,
  sendToInsuredFax,
  setSendToInsuredFax,
  sendToFax,
  setSendToFax,
  onCoverPageSave,
  selectedMasterCertificates,
  setSelectedMasterCertificates,
}: Step1Props, ref: React.Ref<Step1Ref>) {
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const [loadingClient, setLoadingClient] = React.useState(false);
  const [insuredHolderEmails, setInsuredHolderEmails] = React.useState<string[]>([]);
  const [insuredHolderEmailOptions, setInsuredHolderEmailOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [showPhoneBook, setShowPhoneBook] = React.useState(false);
  const [insuredEmailOptions, setInsuredEmailOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [coverPageChecked, setCoverPageChecked] = React.useState(false);
  type HolderSuggestion = { label: string; value: string; _raw?: any };
  const [certificateHolderSuggestions, setCertificateHolderSuggestions] = React.useState<HolderSuggestion[]>([]);
  const [showAddHolder, setShowAddHolder] = React.useState(false);
  const [showFaxCoverPage, setShowFaxCoverPage] = React.useState(false);
  const [coverPageData, setCoverPageData] = React.useState<CoverPageData | null>(null);
  const [masterCertificateOptions, setMasterCertificateOptions] = React.useState<{ label: string; value: string }[]>([]);
  const [masterCertificatesRaw, setMasterCertificatesRaw] = React.useState<any[]>([]);
  const [loadingMaster, setLoadingMaster] = React.useState(true);
  const [masterCertError, setMasterCertError] = React.useState<string | null>(null);
  const masterCertRef = React.useRef<HTMLDivElement>(null);
  const agencyCache = React.useRef<{ name: string; address: string; phone: string; fax: string } | null>(null);

  const fetchAndCacheAgency = React.useCallback(async () => {
    if (agencyCache.current) return agencyCache.current;
    try {
      const res = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/agency-details`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
          NCToken: localStorage.getItem('NCToken') || '',
          Accept: 'application/json',
        },
      });
      if (!res.ok) return null;
      const json = await res.json();
      const agency = json?.value?.[0];
      if (!agency) return null;
      const mailingAddress = [agency.mailingAddress1, agency.mailingAddress2, agency.mailingAddressCity, agency.mailingAddressState, agency.mailingAddressZip].filter(Boolean).join(', ');
      agencyCache.current = { name: agency.name ?? '', address: mailingAddress, phone: agency.phone ?? '', fax: agency.fax ?? '' };
      return agencyCache.current;
    } catch {
      return null;
    }
  }, []);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const [sendMethodError, setSendMethodError] = React.useState<string | null>(null);
  const sendMethodRef = React.useRef<HTMLDivElement>(null);
  const [showMassAction, setShowMassAction] = React.useState(false);

  const emailRegex = /^[\w.+-]+@[\w-]+(\.[\w-]+)*\.[a-zA-Z]{2,}$/;
  const validateEmails = (value: string) => value.split(',').map(e => e.trim()).filter(Boolean).every(e => emailRegex.test(e));

  React.useImperativeHandle(ref, () => ({
    validate: () => {
      if (!selectedMasterCertificates || selectedMasterCertificates.length === 0) {
        setMasterCertError('Master certificate is required.');
        masterCertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      setMasterCertError(null);

      const hasInsuredEmail = sendToInsuredEmail && copyToInsuredEmail.length > 0;
      const hasInsuredFax = sendToInsuredFax && !!insuredFax.trim();

      if (!sendToEmail && !sendToFax && !hasInsuredEmail && !hasInsuredFax) {
        setSendMethodError('Please select Email or Fax and enter the corresponding email address or fax number to proceed.');
        sendMethodRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      const emailMissing = sendToEmail && !email.trim();
      const faxMissing = sendToFax && !fax.trim();
      if (emailMissing || faxMissing) {
        setSendMethodError(
          sendToEmail && sendToFax
            ? 'Please enter the email address and fax number to proceed.'
            : 'Please enter the email address or fax number to proceed.'
        );
        sendMethodRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      setSendMethodError(null);

      if (sendToEmail && email && !validateEmails(email)) {
        setEmailError('Please enter valid email address(es), separated by commas.');
        emailInputRef.current?.focus();
        return false;
      }
      return true;
    },
  }));
  const { client: contextClient } = useSelectedClient();


  React.useEffect(() => {
    async function fetchAndPrefillClient() {
      setLoadingClient(true);
      try {
        if (contextClient) {
          const res = await fetch(
            `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/customer-by-id?insuredDatabaseId=${contextClient.id}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
                'Content-Type': 'application/json',
                NCToken: localStorage.getItem('NCToken') || '',
              },
            }
          );
          const data = await res.json();
          const detail = data?.value?.[0] ?? (Array.isArray(data) ? data[0] : data);
          const emails: string[] = [detail?.eMail, detail?.eMail2, detail?.eMail3]
            .filter(Boolean) as string[];
          setInsuredHolderEmailOptions(emails.map((e: string) => ({ label: e, value: e })));
          if (emails.length > 0) {
            setInsuredHolderEmails([emails[0]]);
            setCopyToInsuredEmail([emails[0]]);
          } else {
            setInsuredHolderEmails([]);
            setCopyToInsuredEmail([]);
          }
          const nextFax = formatUsPhone(detail?.fax || "");
          setInsuredFax(nextFax);
          if (onClientDetails) {
            onClientDetails({
              firstName: detail?.firstName || "",
              lastName: detail?.lastName || "",
              commercialName: detail?.commercialName || "",
              stateName: detail?.stateNameOrAbbreviation || detail?.state || "",
            });
          }
        } else {
          setInsuredHolderEmailOptions([]);
          setInsuredHolderEmails([]);
          setCopyToInsuredEmail([]);
          setFax("");
          setInsuredFax("");
        }
      } catch {
        setInsuredHolderEmailOptions([]);
        setInsuredHolderEmails([]);
        setCopyToInsuredEmail([]);
        setFax("");
        setInsuredFax("");
      }
      setLoadingClient(false);
    }
    fetchAndPrefillClient();
  }, [contextClient?.id, setCopyToInsuredEmail]);

  React.useEffect(() => {
    setInsuredHolderEmails(copyToInsuredEmail);
  }, [copyToInsuredEmail]);

  React.useEffect(() => {
    if (setIsEmailValid) setIsEmailValid(!!email && validateEmails(email));
  }, [email, setIsEmailValid]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (!value) {
      setEmailError(null);
    } else if (!validateEmails(value)) {
      setEmailError('Please enter valid email address(es), separated by commas.');
    } else {
      setEmailError(null);
    }
    if (value.trim()) setSendMethodError(null);
  };

  // Policy IDs from the currently selected master certificate
  const selectedMasterCertPolicyIds = React.useMemo(() => {
    if (!selectedMasterCertificates[0]) return [];
    const mc = masterCertificatesRaw.find((c: any) => c.databaseId === selectedMasterCertificates[0]);
    return mc?.policies?.map((p: any) => p.databaseId as string) ?? [];
  }, [selectedMasterCertificates, masterCertificatesRaw]);

  // Building IDs from the currently selected master certificate (returned directly by the API)
  const selectedMasterCertBuildingIds = React.useMemo(() => {
    if (!selectedMasterCertificates[0]) return [];
    const mc = masterCertificatesRaw.find((c: any) => c.databaseId === selectedMasterCertificates[0]);
    return mc?.buildingIds ?? [];
  }, [selectedMasterCertificates, masterCertificatesRaw]);

  const completeCertificateHolder = async (event: any) => {
    const query = event.query;
    if (query.length > 1) {
      try {
        const NCToken = localStorage.getItem('NCToken');
        const igtoken = localStorage.getItem('igtoken');
        const response = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-holders?top=10&search=${encodeURIComponent(query)}`, {
          headers: {
            'NCToken': NCToken || '',
            'Authorization': `Bearer ${igtoken || ''}`,
          },
        });
        await handleApiResponse(response);
        const data = await response.json();
        const all: HolderSuggestion[] = data.value.map((item: any) => ({ label: item.name, value: item.databaseId, _raw: item }));

        const ncUserId = localStorage.getItem('NCUserId');
        if (selectedMasterCertPolicyIds.length > 0) {
          const filtered = all.filter(item => {
            // Only consider AI entries that belong to the current insured
            const aiDetails: any[] = (item._raw?.additionalInsuredDetails ?? [])
              .filter((ai: any) => ai.truckingCompanyId === insuredDatabaseId);
            // Not an additional insured for this insured → show only if lastChangeUserId matches current user
            if (aiDetails.length === 0) return item._raw?.lastChangeUserId === ncUserId;
            // Is an additional insured for this insured → show only if policy AND building match
            return aiDetails.some((ai: any) => {
              const aiPolicyIds: string[] = (ai.policies ?? []).map((p: any) => p.databaseId as string);
              const policyMatch = aiPolicyIds.some(pid => selectedMasterCertPolicyIds.includes(pid));
              if (!policyMatch) return false;
              if (selectedMasterCertBuildingIds.length > 0) {
                const aiBuildingIds: string[] = ai.buildingIds ?? [];
                if (aiBuildingIds.length > 0) {
                  return aiBuildingIds.some(bid => selectedMasterCertBuildingIds.includes(bid));
                }
              }
              return true;
            });
          });
          setCertificateHolderSuggestions(filtered);
        } else {
          const filtered = all.filter(item => {
            const aiDetails: any[] = (item._raw?.additionalInsuredDetails ?? [])
              .filter((ai: any) => ai.truckingCompanyId === insuredDatabaseId);
            if (aiDetails.length === 0) return item._raw?.lastChangeUserId === ncUserId;
            return true;
          });
          setCertificateHolderSuggestions(filtered);
        }
      } catch {
      }
    }
  };

  React.useEffect(() => {
    if (!insuredDatabaseId) {
      setLoadingMaster(false);
      return;
    }
    setLoadingMaster(true);
    const ncToken = localStorage.getItem('NCToken') || '';
    const authToken = localStorage.getItem('igtoken') || '';
    fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/master-certificates?insuredDatabaseId=${insuredDatabaseId}`, {
      headers: {
        'NCToken': ncToken,
        'Accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    })
      .then(res => handleApiResponse(res))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.value)) {
          const activeCerts = data.value.filter((cert: any) => cert.active !== false);
          setMasterCertificatesRaw(activeCerts);
          setMasterCertificateOptions(activeCerts.map((cert: any) => ({ label: cert.name, value: cert.databaseId })));
        } else {
          setMasterCertificatesRaw([]);
          setMasterCertificateOptions([]);
        }
      })
      .catch(() => setMasterCertificateOptions([]))
      .finally(() => setLoadingMaster(false));
  }, [insuredDatabaseId]);

  React.useEffect(() => {
    const last = certificateHolder[certificateHolder.length - 1];
    if (last) {
      const holder = certificateHolderSuggestions.find(h => h.value === last.value);
      if (holder && holder._raw) {
        setEmail(holder._raw.eMail || "");
        setFax(formatUsPhone(holder._raw.fax || ""));
        setInsuredFax(formatUsPhone(holder._raw.fax || ""));
        if (holder._raw.eMail) setSendToEmail(true);
        setSendToFax(true);
      }
    }
  }, [certificateHolder, certificateHolderSuggestions]);

  return (
      <div className="page">
        <div className="page__sectionHeader">
          <div>
            <h2 className="page__title">General</h2>
            <p className="page__muted">Who should receive the certificate?</p>
          </div>
        </div>
        <div className="page__twoCol" style={{ gridTemplateColumns: '1fr' }}>
          <div className="pageCard" style={{ padding: 16 }}>
            <div ref={masterCertRef} style={{ marginBottom: 14 }}>
            <div className="page__muted" style={{ marginBottom: 4, color: '#002A5C', fontWeight: 500 }}>
              Master Certificate <span style={{ color: 'red', marginLeft: 2 }}>*</span>
            </div>
            <div style={{ position: 'relative' }}>
              <Dropdown
                value={selectedMasterCertificates[0] ?? null}
                options={masterCertificateOptions}
                onChange={e => { setSelectedMasterCertificates(e.value ? [e.value] : []); if (e.value) setMasterCertError(null); }}
                placeholder={loadingMaster ? 'Loading...' : 'Select master certificate'}
                filter
                style={{ width: '100%' }}
                className={masterCertError ? 'p-invalid' : ''}
                disabled={loadingMaster}
              />
              {loadingMaster && (
                <ProgressSpinner style={{ width: '20px', height: '20px', position: 'absolute', right: '40px', top: '50%', transform: 'translateY(-50%)' }} />
              )}
            </div>
            {masterCertError && <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{masterCertError}</div>}
          </div>
          <div className="page__muted" style={{ marginBottom: 4, color: '#002A5C', fontWeight: 500 }}>Select certificate holder</div>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: 14, gap: 8 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <i className="pi pi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 13, zIndex: 1, pointerEvents: 'none' }} />
                <AutoComplete
                  value={certificateHolder}
                  suggestions={certificateHolderSuggestions}
                  completeMethod={completeCertificateHolder}
                  onChange={(e) => setCertificateHolder(Array.isArray(e.value) ? e.value : [])}
                  placeholder={certificateHolder.length === 0 ? 'Select certificate holder' : ''}
                  field="label"
                  multiple
                  className="cert-holder-ac cert-holder-ac--icon"
                  panelClassName="custom-ac-panel"
                  itemTemplate={(item: any) => {
                    if (!item) return null;
                    const r = item._raw || {};
                    const location = [r.city, r.stateName].filter(Boolean).join(', ');
                    const address = [r.addressLine1, location, r.zipCode].filter(Boolean).join(' · ');
                    const aiDetails: any[] = (r.additionalInsuredDetails ?? [])
                      .filter((ai: any) => ai.truckingCompanyId === insuredDatabaseId);
                    const isAdditionalInsured = selectedMasterCertPolicyIds.length > 0 && aiDetails.length > 0 &&
                      aiDetails.some((ai: any) => {
                        const policyMatch = (ai.policies ?? []).map((p: any) => p.databaseId as string)
                          .some((pid: string) => selectedMasterCertPolicyIds.includes(pid));
                        if (!policyMatch) return false;
                        if (selectedMasterCertBuildingIds.length > 0) {
                          const aiBuildingIds: string[] = ai.buildingIds ?? [];
                          if (aiBuildingIds.length > 0) {
                            return aiBuildingIds.some(bid => selectedMasterCertBuildingIds.includes(bid));
                          }
                        }
                        return true;
                      });
                    return (
                      <div className="custom-ac-item custom-ac-item--rich">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div className="custom-ac-item__name">{item.label}</div>
                          {isAdditionalInsured && (
                            <span style={{ fontSize: 10, fontWeight: 600, color: '#0057A4', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 4, padding: '1px 6px', whiteSpace: 'nowrap', lineHeight: '16px' }}>
                              Additional Insured
                            </span>
                          )}
                        </div>
                        {address && <div className="custom-ac-item__address">{address}</div>}
                      </div>
                    );
                  }}
                />
              </div>
              <button
                type="button"
                className="p-button p-component carrierDialog__secondaryBtn"
                style={{ whiteSpace: 'nowrap', height: 36, background: '#0057A4', borderColor: '#0057A4', color: '#fff' }}
                onClick={() => setShowAddHolder(true)}
              >
                + Add certificate holder
              </button>
            </div>
            {/* Send a copy to me */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <Checkbox inputId="ccToMe" checked={ccToMe} onChange={e => setCcToMe(e.checked ?? false)} />
              <label htmlFor="ccToMe" className="page__muted">Send a copy to me</label>
            </div>

            <div ref={sendMethodRef}>
            {/* Send to email */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: emailError ? 4 : 10 }}>
              <Checkbox inputId="sendToEmail" checked={sendToEmail} onChange={e => { const checked = e.checked ?? false; setSendToEmail(checked); if (!checked) setEmailError(null); if (checked || sendToFax) setSendMethodError(null); }} />
              <label htmlFor="sendToEmail" className="page__muted" style={{ whiteSpace: 'nowrap', minWidth: 100 }}>Send to email</label>
              {sendToEmail && (
                <span className="p-input-icon-left" style={{ flex: 1 }}>
                  <i className="pi pi-envelope" />
                  <InputText maxLength={500}
                    ref={emailInputRef}
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="recipient@company.com, another@company.com"
                    style={{ width: '100%' }}
                    className={emailError ? 'p-invalid' : ''}
                  />
                </span>
              )}
            </div>
            {emailError && <div style={{ color: 'red', marginBottom: 8, fontSize: 10 }}>{emailError}</div>}

            {/* Send to fax */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Checkbox inputId="sendToFax" checked={sendToFax} onChange={e => { const checked = e.checked ?? false; setSendToFax(checked); if (checked || sendToEmail) setSendMethodError(null); }} />
              <label htmlFor="sendToFax" className="page__muted" style={{ whiteSpace: 'nowrap', minWidth: 100 }}>Send to fax</label>
              {sendToFax && (
                <span className="p-input-icon-left" style={{ flex: 1, position: 'relative' }}>
                  <i className="pi pi-phone" />
                  <InputText maxLength={20}
                    value={fax}
                    onChange={e => { setFax(maskUsPhone(e.target.value)); if (e.target.value.trim()) setSendMethodError(null); }}
                    placeholder="Enter fax number"
                    style={{ width: '100%' }}
                    disabled={loadingClient}
                  />
                  {loadingClient && (
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                      <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" />
                    </span>
                  )}
                </span>
              )}
              {/* Phone Book hidden
              {sendToFax && (
                <Button
                  type="button"
                  label="Phone Book"
                  className="carrierDialog__secondaryBtn"
                  style={{ height: 36, fontSize: 13, padding: '0 12px', whiteSpace: 'nowrap' }}
                  onClick={() => setShowPhoneBook(true)}
                />
              )}
              */}
            </div>
            {sendMethodError && <div style={{ color: 'red', marginBottom: 8, fontSize: 12 }}>{sendMethodError}</div>}
            </div>

            {sendToFax && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Checkbox
                    inputId="coverPage"
                    checked={coverPageChecked}
                    onChange={e => {
                      const checked = e.checked ?? false;
                      setCoverPageChecked(checked);
                      if (!checked) {
                        setCoverPageData(null);
                        onCoverPageSave(null);
                      } else if (!coverPageData) {
                        fetchAndCacheAgency().then(agency => {
                          if (!agency) return;
                          const today = new Date();
                          const todayStr = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}/${today.getFullYear()}`;
                          const defaultData: CoverPageData = {
                            companyName: agency.name,
                            address: agency.address,
                            phone: agency.phone,
                            fax: agency.fax,
                            to: '',
                            toFaxNumber: '',
                            date: todayStr,
                            from: agency.name,
                            fromFaxNumber: agency.fax,
                            memo: '',
                          };
                          setCoverPageData(defaultData);
                          onCoverPageSave(buildCoverPageXml(defaultData));
                        });
                      }
                    }}
                  />
                  <label htmlFor="coverPage" className="page__muted">Add cover page</label>
                  {coverPageChecked && (
                    <Button
                      type="button"
                      label="View/edit cover page"
                      className="carrierDialog__secondaryBtn"
                      style={{ height: 32, fontSize: 13, padding: '0 10px', whiteSpace: 'nowrap' }}
                      onClick={() => setShowFaxCoverPage(true)}
                    />
                  )}
                  {coverPageChecked && coverPageData && (
                    <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <i className="pi pi-check-circle" style={{ fontSize: 12 }} /> Saved
                    </span>
                  )}
                </div>
                <PhoneBook
                  visible={showPhoneBook}
                  onHide={() => setShowPhoneBook(false)}
                  onSelect={(data: { email: string; fax: string }) => { setEmail(data.email); setFax(data.fax); }}
                />
                <FaxCoverPage
                  visible={showFaxCoverPage}
                  onHide={() => setShowFaxCoverPage(false)}
                  onSave={(data, xml) => { setCoverPageData(data); onCoverPageSave(xml); }}
                  initialData={coverPageData ?? undefined}
                />
              </>
            )}
            {/* Send copy to insured email */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Checkbox
                inputId="sendToInsuredEmail"
                checked={sendToInsuredEmail}
                onChange={e => setSendToInsuredEmail(e.checked ?? false)}
              />
              <label htmlFor="sendToInsuredEmail" className="page__muted" style={{ whiteSpace: 'nowrap', minWidth: 160 }}>Send copy to insured email</label>
              {sendToInsuredEmail && (
                <div style={{ flex: 1, position: 'relative' }}>
                  <i className="pi pi-envelope" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 13, zIndex: 1, pointerEvents: 'none' }} />
                  <MultiSelect
                    value={insuredHolderEmails}
                    options={insuredHolderEmailOptions}
                    onChange={e => {
                      setInsuredHolderEmails(e.value);
                      setCopyToInsuredEmail(e.value);
                    }}
                    placeholder="Select insured email"
                    style={{ width: '100%' }}
                    appendTo={document.body}
                    emptyMessage="No available options"
                    className="cert-holder-ms cert-holder-ms--icon"
                    display="chip"
                  />
                </div>
              )}
            </div>

            {/* Send copy to insured fax */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <Checkbox
                inputId="sendToInsuredFax"
                checked={sendToInsuredFax}
                onChange={e => {
                  setSendToInsuredFax(e.checked ?? false);
                  if (!e.checked) setInsuredFax('');
                }}
              />
              <label htmlFor="sendToInsuredFax" className="page__muted" style={{ whiteSpace: 'nowrap', minWidth: 160 }}>Send copy to insured fax</label>
              {sendToInsuredFax && (
                <span className="p-input-icon-left" style={{ flex: 1, position: 'relative' }}>
                  <i className="pi pi-phone" />
                  <InputText maxLength={20}
                    value={insuredFax}
                    onChange={e => setInsuredFax(maskUsPhone(e.target.value))}
                    placeholder="Insured fax number"
                    style={{ width: '100%' }}
                    disabled={loadingClient}
                  />
                  {loadingClient && (
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                      <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" />
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        {/* <div style={{ marginTop: 16 }}>
          <div
            className="pageCard"
            style={{
              padding: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => setShowMassAction(prev => !prev)}
          >
            <span className="page__muted" style={{ fontWeight: 600, color: '#002A5C' }}>
              Mass Action
            </span>
            <i className={showMassAction ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} />
          </div>
          {showMassAction && (
            <div style={{ marginTop: 12 }}>
              <Step6MassSection
                insuredDatabaseId={insuredDatabaseId}
                selectedSentCertIds={selectedSentCertIds}
                setSelectedSentCertIds={setSelectedSentCertIds}
              />
            </div>
          )}
        </div> */}
        <AddCertificateHolder visible={showAddHolder} onHide={() => setShowAddHolder(false)} insuredDatabaseId={insuredDatabaseId} />
      </div>
  );
});
export default Step1;